import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Crown, Archive as ArchiveIcon, Play, Calendar } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";

export default function Archive() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, locked: false, error: null });
  const [puzzles, setPuzzles] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login"); return; }
    let cancel = false;
    (async () => {
      setState({ loading: true, locked: false, error: null });
      try {
        const { data } = await api.get("/game/archive");
        if (cancel) return;
        setPuzzles(data.puzzles || []);
        setState({ loading: false, locked: false, error: null });
      } catch (e) {
        if (cancel) return;
        if (e?.response?.status === 403) setState({ loading: false, locked: true, error: apiError(e) });
        else setState({ loading: false, locked: false, error: apiError(e) });
      }
    })();
    return () => { cancel = true; };
  }, [user, authLoading, navigate]);

  const zones = [["casual", "New Jack"], ["golden_era", "Triple OG"]];
  const shown = filter === "all"
  ? puzzles
  : puzzles.filter((p) =>
      filter === "golden_era"
        ? ["golden_era", "new_and_now"].includes(p.zone)
        : p.zone === filter
    );

  if (state.loading) return <Layout><div className="py-32 text-center font-mono-caps text-xs text-primary animate-pulse">Loading the archive…</div></Layout>;

  if (state.locked) return (
    <Layout>
      <div className="max-w-lg mx-auto px-5 py-24 text-center" data-testid="archive-locked">
        <Lock className="mx-auto text-primary mb-6" size={40} />
        <h1 className="font-display text-4xl mb-3">PREMIUM ARCHIVE</h1>
        <p className="text-muted-foreground mb-8">{state.error || "The Puzzle Archive is a Premium feature."} Unlock every past puzzle across all zones for ${"2.99"}/mo.</p>
        <button onClick={() => navigate("/premium")} data-testid="archive-upgrade" className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-mono-caps text-xs px-6 py-3.5 border border-primary hover-lift">
          <Crown size={15} /> Go Premium
        </button>
      </div>
    </Layout>
  );

  if (state.error) return <Layout><div className="py-32 text-center text-muted-foreground">{state.error}</div></Layout>;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10" data-testid="archive-page">
        <div className="flex items-center gap-3 mb-2">
          <ArchiveIcon className="text-primary" size={26} />
          <h1 className="font-display text-4xl sm:text-5xl">PUZZLE ARCHIVE</h1>
        </div>
        <p className="text-muted-foreground text-sm mb-8">Replay any previously published puzzle from every zone. {puzzles.length} puzzles available.</p>

        <div className="flex flex-wrap gap-2 mb-8">
          <button onClick={() => setFilter("all")} data-testid="archive-filter-all"
            className={`font-mono-caps text-[10px] px-4 py-2 border transition-colors ${filter === "all" ? "bg-primary text-primary-foreground border-primary" : "border-white/20 text-muted-foreground hover:text-foreground"}`}>
            All
          </button>
          {zones.map(([zid, zname]) => (
            <button key={zid} onClick={() => setFilter(zid)} data-testid={`archive-filter-${zid}`}
              className={`font-mono-caps text-[10px] px-4 py-2 border transition-colors ${filter === zid ? "bg-primary text-primary-foreground border-primary" : "border-white/20 text-muted-foreground hover:text-foreground"}`}>
              {zname}
            </button>
          ))}
        </div>

        {shown.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground font-mono-caps text-xs">No puzzles in this zone yet.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {shown.map((p) => (
              <div key={p.puzzle_id} data-testid={`archive-item-${p.puzzle_id}`}
                className="border border-white/15 bg-card p-5 flex flex-col gap-3 hover:border-primary/50 transition-colors">
                <div className="flex items-center justify-between">
                  <span className="font-mono-caps text-[9px] text-primary border border-primary/40 px-2 py-1">{p.zone_name}</span>
                  <span className="font-mono-caps text-[9px] text-muted-foreground uppercase">{p.difficulty}</span>
                </div>
                <div>
                  <h3 className="font-display text-2xl leading-none">{p.title}</h3>
                  <div className="flex items-center gap-2 mt-2 font-mono-caps text-[10px] text-muted-foreground">
                    <Calendar size={12} /> {p.date || "—"} · {p.word_count} words · #{p.puzzle_id.slice(-4)}
                  </div>
                </div>
                <button onClick={() => navigate(`/play/${p.zone}?puzzle_id=${p.puzzle_id}`)} data-testid={`archive-play-${p.puzzle_id}`}
                  className="mt-1 inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-mono-caps text-[10px] px-4 py-2.5 border border-primary hover-lift">
                  <Play size={13} /> Play
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
